import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setTest360Data, updateCompetenceProgress, loadExistingAnswers } from 'ducks/test360/reducer'
import { selectTest360Competences, selectTest360EvaluatedUsers } from 'ducks/test360/selectors'
import { Test360ApiResponse, Test360Competence, Test360EvaluatedUser, Competence, PersonToEvaluate } from '../types'
import { convertApiAnswersToRedux } from '../utils/convertApiAnswersToRedux'

export const useTest360Data = () => {
  const dispatch = useDispatch()
  const apiCompetences = useSelector(selectTest360Competences)
  const apiEvaluatedUsers = useSelector(selectTest360EvaluatedUsers)

  // Convert API data to internal format
  const convertedCompetences = useMemo((): Competence[] => {
    return apiCompetences.map((apiComp: Test360Competence) => ({
      id: apiComp.competenceId,
      name: apiComp.name,
      description: apiComp.description,
      questions: apiComp.questions.map((q) => q.question),
      answeredQuestions: apiComp.answeredQuestions,
      isCompleted: apiComp.isComplete,
      isEnabled: apiComp.isEnabled,
    }))
  }, [apiCompetences])

  const convertedEvaluatedUsers = useMemo((): PersonToEvaluate[] => {
    return apiEvaluatedUsers.map((apiUser: Test360EvaluatedUser) => ({
      id: apiUser.assignment_id.toString(),
      name: apiUser.is_self_evaluation ? 'Yo' : apiUser.user_name,
      position: apiUser.role_name,
      department: '',
      state: apiUser.status,
      isSelfEvaluation: apiUser.is_self_evaluation,
    }))
  }, [apiEvaluatedUsers])

  const loadTest360Data = useCallback(
    (data: Test360ApiResponse) => {
      dispatch(setTest360Data(data))

      // Convert and load existing answers from API
      if (data.competences && Array.isArray(data.competences)) {
        const reduxAnswers = convertApiAnswersToRedux(data.competences)
        dispatch(loadExistingAnswers(reduxAnswers))
      }
    },
    [dispatch]
  )

  const updateProgress = useCallback(
    (competenceId: number, answeredQuestions: number, isComplete: boolean) => {
      dispatch(updateCompetenceProgress({ competenceId, answeredQuestions, isComplete }))
    },
    [dispatch]
  )

  return {
    competences: convertedCompetences,
    evaluatedUsers: convertedEvaluatedUsers,
    loadTest360Data,
    updateProgress,
  }
}
